import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MoonMark as Moon } from "@/components/MoonMark";
import { api, money, mediaUrl } from "@/lib/api";
import { ASSETS, HOME_TILES } from "@/lib/content";
import MoonDivider from "@/components/MoonDivider";
import ProductCard from "@/components/ProductCard";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products")
      .then((r) => {
        setProducts(r.data);
        setFeatured(r.data.filter((p) => p.featured).slice(0, 4));
      })
      .catch(() => {});
  }, []);

  const tileFor = (key) => products.find((p) => (p.name || "").toLowerCase().includes(key.toLowerCase()));

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[86vh] flex items-center overflow-hidden bg-black">
        <div className="absolute inset-0">
          <img
            src={mediaUrl(ASSETS.heroBg)}
            alt="After Dark Chocolates sculpted collection"
            data-testid="hero-bg"
            className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.92] contrast-[1.06] saturate-[1.08] hero-drift"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
          <div className="hero-sheen absolute inset-0 overflow-hidden pointer-events-none" />
          <div className="absolute inset-0 grain opacity-20" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 w-full">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
            className="max-w-2xl -translate-y-6 sm:-translate-y-10"
          >
            <div className="inline-flex items-center gap-2.5 border border-gold/50 bg-black/40 backdrop-blur-sm px-4 py-2 mb-7" data-testid="now-available-badge">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              <span className="font-body uppercase tracking-[0.3em] text-[11px] text-gold">Now Available</span>
            </div>
            <img
              src={mediaUrl(ASSETS.slogan)}
              alt="Sinfully sculpted for your naughty pleasure"
              data-testid="hero-slogan"
              className="w-full max-w-xl mix-blend-screen mb-6"
            />
            <p className="font-body text-white/75 text-base sm:text-lg leading-relaxed max-w-md mb-9">
              Our collection of sinfully sculpted chocolates is here. Indulge yourself — discreetly shipped worldwide.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/shop"
                data-testid="hero-shop-btn"
                className="px-9 py-4 bg-gold text-black font-body uppercase tracking-[0.2em] text-xs hover:bg-champagne transition-colors duration-300 text-center"
              >
                Shop Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FEATURED COLLECTIONS - bento */}
      <section className="max-w-7xl mx-auto px-6 pt-16 pb-10">
        <MoonDivider label="Shop Our Sculpted Chocolates" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-10">
          {HOME_TILES.map((tile, i) => {
            const product = tileFor(tile.key);
            const imgs = product ? (product.images || []) : [];
            const image = tile.image || imgs[tile.imageIndex] || imgs[0] || null;
            const to = product ? `/product/${product.id}` : "/shop";
            return (
              <motion.div
                key={tile.key}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <Link
                  to={to}
                  data-testid={`collection-tile-${i}`}
                  className="group block relative h-80 overflow-hidden border border-white/5"
                >
                  {image && <img src={mediaUrl(image)} alt={tile.title} className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110" />}
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-black/20" />
                  <div className="absolute inset-0 p-7 flex flex-col justify-end">
                    <h3 className="font-serif text-3xl text-cream leading-tight mb-2">{tile.title}</h3>
                    <p className="font-body text-sm text-white/60 mb-4 leading-relaxed">{tile.tagline}</p>
                    <span className="inline-flex items-center gap-2 font-body uppercase tracking-[0.2em] text-xs text-gold">
                      Discover <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      {featured.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <MoonDivider label="Most Desired" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-8 mt-10">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
          <div className="text-center mt-12">
            <Link to="/shop" className="inline-flex items-center gap-2 font-body uppercase tracking-[0.25em] text-xs text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors" data-testid="view-all-btn">
              View The Full Boutique <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
